/**
 * Feedback Campaigns API Service
 *
 * Phase 1 of the Upward Feedback Campaigns project (May 2026).
 * Backend mounted at `/api/v1/admin/feedback-campaigns/...`.
 */

import { apiClient } from '../apiClient'

// ==================== Types ====================

export type CampaignStatus = 'draft' | 'scheduled' | 'launched' | 'closed' | 'cancelled'
export type SubjectRelationship = 'line_manager' | 'campus_general_manager' | 'organisation' | 'custom'

export interface AudienceFilter {
  employment_types: string[]
  campuses: string[]
  excluded_user_ids: string[]
  included_user_ids: string[]
}

export interface FeedbackCampaign {
  id: string
  name: string
  description?: string | null
  template_id: string
  status: CampaignStatus
  audience_filter: AudienceFilter | Record<string, any>
  subject_relationship: SubjectRelationship
  launch_at?: string | null
  close_at: string
  /**
   * Deadline shown to respondents (emails + in-app due date). Null means
   * communicate `close_at`. Set earlier than close_at to publish a deadline
   * while quietly staying open for late responses.
   */
  display_close_at?: string | null
  reminder_lead_days: number
  launched_at?: string | null
  closed_at?: string | null
  reminder_sent_at?: string | null  // first (mid-cycle) reminder
  second_reminder_sent_at?: string | null  // second (closing-week) reminder
  test_recipient_user_ids: string[]
  test_launched_at?: string | null
  /** When this campaign was launched as part of a bundle, the OTHER members'
   *  IDs live here. Drives consolidated invites + reminders. */
  linked_campaign_ids: string[]
  created_at: string
  updated_at: string
  recipient_count: number
  response_count: number
  test_recipient_count: number
  /** How many recipient rows have a non-null invitation_sent_at. When this
   *  is less than (recipient_count + test_recipient_count) after a launch,
   *  HR can call retryUndeliveredInvites to resend the undelivered subset
   *  without re-emailing recipients who already received their invite. */
  invitations_delivered_count: number
}

/**
 * Background PDF-report ZIP build. Created via createReportJob, polled
 * via getReportJob until status is 'complete' (then downloadReportJob)
 * or 'failed'. `stale: true` means the build stopped heartbeating
 * (worker replica died) — offer the user a rebuild.
 */
export interface CampaignReportJob {
  job_id: string
  campaign_id: string
  status: 'pending' | 'running' | 'complete' | 'failed'
  built_count: number
  total_count: number
  failed_subjects: number
  include_test: boolean
  min_n: number
  error?: string | null
  zip_filename?: string | null
  zip_size_bytes?: number | null
  created_at?: string | null
  started_at?: string | null
  finished_at?: string | null
  stale: boolean
}

export interface BundleLaunchSummary {
  campaign_ids: string[]
  is_test_launch: boolean
  invitations_queued: number
  per_campaign: Record<string, {
    recipients_created: number
    requests_created: number
    skipped_no_manager: number
    skipped_no_subject: number
  }>
}

export interface CampaignCreate {
  name: string
  description?: string
  template_id: string
  close_at: string
  /** Deadline shown to respondents; omit to communicate `close_at`. */
  display_close_at?: string | null
  reminder_lead_days?: number
  audience_filter?: Partial<AudienceFilter>
  subject_relationship?: SubjectRelationship
  test_recipient_user_ids?: string[]
  launch_at?: string | null
}

export interface CampaignUpdate {
  name?: string
  description?: string
  template_id?: string
  close_at?: string
  display_close_at?: string | null
  reminder_lead_days?: number
  audience_filter?: Partial<AudienceFilter>
  subject_relationship?: SubjectRelationship
  test_recipient_user_ids?: string[]
  launch_at?: string | null
}

/**
 * Cohort member produced by /preview-audience.
 *
 * The backend returns the eligible-pair shape: each entry pairs a
 * respondent with their resolved subject (line manager / CGM). We
 * surface both ends so the HR audience-builder can sanity-check
 * mapping before launch.
 */
export interface AudienceSampleMember {
  respondent_user_id: string
  respondent_display_name?: string | null
  respondent_campus?: string | null
  respondent_position_type?: string | null
  subject_user_id?: string | null
  subject_display_name?: string | null
}

export interface AudiencePreview {
  eligible_count: number
  excluded_no_manager: number
  excluded_no_subject: number
  excluded_explicit: number
  sample: AudienceSampleMember[]
  total_potential: number
}

export interface LaunchSummary {
  campaign_id: string
  is_test_launch: boolean
  recipients_created: number
  requests_created: number
  invitations_queued: number
  skipped_no_manager: number
  skipped_no_subject: number
}

/**
 * Anonymised progress for the monitor view. Backend keeps this
 * deliberately coarse — no per-respondent or per-subject breakdowns —
 * because anonymity is the entire point of upward feedback campaigns.
 */
/**
 * Response counts for one employment type (Permanent, FTC, Casual, IC,
 * Unclassified). Test recipients are excluded server-side.
 */
export interface CampaignSegment {
  key: string
  label: string
  recipient_count: number
  response_count: number
}

export interface CampaignProgress {
  campaign_id: string
  name: string
  status: CampaignStatus
  close_at: string | null
  recipient_count: number
  response_count: number
  /**
   * Per-employment-type breakdown. Empty when the identity directory was
   * unreachable (progress still renders — just without the split).
   */
  segments?: CampaignSegment[]
  /**
   * Roll-up for the employment types HR reports on (permanent + FTC) —
   * ICs and casuals participate voluntarily, so blending them into one
   * percentage understates engagement among those actually asked to take part.
   */
  core?: {
    label: string
    keys: string[]
    recipient_count: number
    response_count: number
  } | null
}

/**
 * Admin-side recipient row. `has_response` is a boolean ("yes/no
 * submitted"); the actual response content is intentionally not
 * exposed to the admin to preserve respondent anonymity.
 */
export interface CampaignRecipient {
  campaign_id: string
  respondent_user_id: string
  subject_user_id: string
  feedback_request_id: string
  feedback_stakeholder_id: string
  is_test: boolean
  invitation_sent_at?: string | null
  reminder_sent_at?: string | null
  has_response: boolean
}

/**
 * Subject-side status tile shape — what a manager / CGM sees about
 * upward feedback campaigns targeting *them*. No respondent identities.
 */
export interface UserCampaignSubject {
  campaign_id: string
  name: string
  status: CampaignStatus
  close_at: string | null
  recipient_count: number
  response_count: number
  subject_user_id: string
}

// ==================== Service ====================

const BASE = '/api/v1/admin/feedback-campaigns'

const feedbackCampaignsService = {
  async listCampaigns(statusFilter?: CampaignStatus): Promise<FeedbackCampaign[]> {
    const params = statusFilter ? `?status_filter=${statusFilter}` : ''
    const response = await apiClient.get<FeedbackCampaign[]>(`${BASE}/campaigns${params}`)
    return response.data
  },

  async getCampaign(campaignId: string): Promise<FeedbackCampaign> {
    const response = await apiClient.get<FeedbackCampaign>(`${BASE}/campaigns/${campaignId}`)
    return response.data
  },

  async createCampaign(payload: CampaignCreate): Promise<FeedbackCampaign> {
    const response = await apiClient.post<FeedbackCampaign>(`${BASE}/campaigns`, payload)
    return response.data
  },

  async updateCampaign(campaignId: string, payload: CampaignUpdate): Promise<FeedbackCampaign> {
    const response = await apiClient.patch<FeedbackCampaign>(`${BASE}/campaigns/${campaignId}`, payload)
    return response.data
  },

  async cancelCampaign(campaignId: string): Promise<FeedbackCampaign> {
    const response = await apiClient.post<FeedbackCampaign>(`${BASE}/campaigns/${campaignId}/cancel`)
    return response.data
  },

  async previewAudience(
    campaignId: string,
    filterOverride?: Partial<AudienceFilter>,
  ): Promise<AudiencePreview> {
    const response = await apiClient.post<AudiencePreview>(
      `${BASE}/campaigns/${campaignId}/preview-audience`,
      filterOverride ?? null,
    )
    return response.data
  },

  async launchCampaign(campaignId: string): Promise<LaunchSummary> {
    const response = await apiClient.post<LaunchSummary>(`${BASE}/campaigns/${campaignId}/launch`)
    return response.data
  },

  /**
   * Launch two-or-more campaigns as a single cycle. Respondents qualifying
   * for multiple campaigns receive ONE consolidated invite email; each
   * campaign retains its own audience/template/close_at/status.
   */
  async launchCampaignBundle(campaignIds: string[]): Promise<BundleLaunchSummary> {
    const response = await apiClient.post<BundleLaunchSummary>(
      `${BASE}/campaign-bundles/launch`,
      { campaign_ids: campaignIds },
    )
    return response.data
  },

  async testLaunchCampaignBundle(
    campaignIds: string[],
    respondentUserIds: string[],
  ): Promise<BundleLaunchSummary> {
    const response = await apiClient.post<BundleLaunchSummary>(
      `${BASE}/campaign-bundles/test-launch`,
      { campaign_ids: campaignIds, respondent_user_ids: respondentUserIds },
    )
    return response.data
  },

  async testLaunchCampaign(
    campaignId: string,
    respondentUserIds: string[],
  ): Promise<LaunchSummary> {
    const response = await apiClient.post<LaunchSummary>(
      `${BASE}/campaigns/${campaignId}/test-launch`,
      { respondent_user_ids: respondentUserIds },
    )
    return response.data
  },

  /**
   * Preview the reminder email HR would dispatch, without sending.
   *
   * Renders the exact (subject, body_html) the recipients would receive
   * — same variant auto-select + bundle linkage as the live send path.
   * Returns the HTML for iframe rendering so Outlook-safe inline styles
   * show as recipients will see them.
   *
   * Variant defaults to the next un-sent one (first → second). Pass
   * `{variant: 'second'}` to preview the closing-week template even when
   * the first hasn't fired yet.
   */
  async previewCampaignReminder(
    campaignId: string,
    opts?: { variant?: 'first' | 'second' | 'final'; requiredOnly?: boolean },
  ): Promise<{
    campaign_id: string
    variant: 'first' | 'second' | 'final'
    subject: string
    body_html: string
    close_at: string | null
    close_str: string
    campaigns_in_bundle: string[]
    survey_links: Array<{ name: string; url: string }>
    /** Who would actually receive it — computed by the same resolver the
     *  send uses, so the number HR approves is the number that gets mailed. */
    audience: {
      required_only: boolean
      recipient_count: number
      outstanding_total: number
      suppressed: number
      outstanding_by_employment_type: Array<{ label: string; count: number }>
    }
  }> {
    const params = new URLSearchParams()
    if (opts?.variant) params.append('variant', opts.variant)
    if (opts?.requiredOnly) params.append('required_only', 'true')
    const qs = params.toString() ? `?${params.toString()}` : ''
    const response = await apiClient.get(
      `${BASE}/campaigns/${campaignId}/reminder-preview${qs}`,
    )
    return response.data as any
  },

  /**
   * Manually trigger a reminder. Variant defaults to the next un-sent one
   * (first → second → both done). Respects bundle linkage automatically.
   *
   * Backend dispatches the email fan-out as a background task so the
   * response returns immediately — `reminders_sent` will be `null` and
   * `queued: true` indicates the dispatcher has accepted the request.
   * Poll `/campaigns/{id}/progress` or refresh the drawer to see the
   * `reminder_sent_at` / `second_reminder_sent_at` timestamps once the
   * background task commits.
   */
  async sendCampaignReminder(
    campaignId: string,
    opts?: { variant?: 'first' | 'second' | 'final'; requiredOnly?: boolean },
  ): Promise<{
    campaign_id: string
    variant: 'first' | 'second' | 'final'
    campaigns_in_bundle: string[]
    reminders_sent: number | null
    required_only?: boolean
    recipient_count?: number
    queued?: boolean
  }> {
    const body: Record<string, unknown> = {}
    if (opts?.variant) body.variant = opts.variant
    if (opts?.requiredOnly) body.required_only = true
    const response = await apiClient.post(
      `${BASE}/campaigns/${campaignId}/send-reminder`,
      body,
    )
    return response.data as any
  },

  /**
   * Re-dispatch invites for any recipient whose original send failed
   * (invitation_sent_at is null on their row). Auto-detects bundle
   * linkage and consolidates per respondent. Idempotent — already-
   * delivered recipients are skipped automatically.
   *
   * Returns the count being queued for re-send; the actual fan-out
   * runs in the background. Poll `getCampaign` to see
   * `invitations_delivered_count` tick up as the bg task progresses.
   */
  async retryUndeliveredInvites(
    campaignId: string,
  ): Promise<{
    campaign_id: string
    mode: 'solo' | 'bundle'
    undelivered_count: number
    queued: boolean
    campaigns_in_bundle?: string[]
  }> {
    const response = await apiClient.post(
      `${BASE}/campaigns/${campaignId}/retry-invites`,
    )
    return response.data as any
  },

  async closeCampaign(campaignId: string): Promise<FeedbackCampaign> {
    const response = await apiClient.post<FeedbackCampaign>(`${BASE}/campaigns/${campaignId}/close`)
    return response.data
  },

  async getProgress(campaignId: string): Promise<CampaignProgress> {
    const response = await apiClient.get<CampaignProgress>(`${BASE}/campaigns/${campaignId}/progress`)
    return response.data
  },

  async listRecipients(campaignId: string): Promise<CampaignRecipient[]> {
    const response = await apiClient.get<CampaignRecipient[]>(
      `${BASE}/campaigns/${campaignId}/recipients`,
    )
    return response.data
  },

  async getUserCampaignSubjects(userId: string): Promise<UserCampaignSubject[]> {
    const response = await apiClient.get<UserCampaignSubject[]>(`${BASE}/users/${userId}/campaign-subjects`)
    return response.data
  },

  /**
   * Start a BACKGROUND build of the per-subject PDF report ZIP.
   *
   * The synchronous reports.zip endpoint can't survive the timeout
   * chain at line-manager scale (200+ subjects × one AI synthesis each
   * ≈ minutes of build with zero bytes flowing — axios aborts at 180s,
   * ACA ingress at 240s). This returns a job descriptor immediately;
   * poll `getReportJob` for progress ("built_count of total_count"),
   * then `downloadReportJob` when status === 'complete'.
   *
   * Idempotent-ish: if a non-stale build is already running for the
   * campaign, that job is returned instead of starting a duplicate.
   */
  async createReportJob(
    campaignId: string,
    opts?: { includeTest?: boolean; minN?: number },
  ): Promise<CampaignReportJob> {
    const response = await apiClient.post(
      `${BASE}/campaigns/${campaignId}/report-jobs`,
      { include_test: opts?.includeTest ?? false, min_n: opts?.minN ?? 3 },
    )
    return response.data as CampaignReportJob
  },

  async getReportJob(campaignId: string, jobId: string): Promise<CampaignReportJob> {
    const response = await apiClient.get(
      `${BASE}/campaigns/${campaignId}/report-jobs/${jobId}`,
    )
    return response.data as CampaignReportJob
  },

  /** Newest job for the campaign, or null when none exist. Lets the UI
   *  resume a progress display after a page reload. The backend returns
   *  200 with a null body for "no jobs" (a 404 here would spam the
   *  browser console on every drawer open); the catch keeps deploy-skew
   *  compatibility with the older 404 behaviour. */
  async getLatestReportJob(campaignId: string): Promise<CampaignReportJob | null> {
    try {
      const response = await apiClient.get(
        `${BASE}/campaigns/${campaignId}/report-jobs/latest`,
      )
      return (response.data ?? null) as CampaignReportJob | null
    } catch (err: any) {
      if (err?.response?.status === 404) return null
      throw err
    }
  },

  /** Download a finished job's archive — fast, just streams stored bytes. */
  async downloadReportJob(
    campaignId: string,
    jobId: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get(
      `${BASE}/campaigns/${campaignId}/report-jobs/${jobId}/download`,
      { responseType: 'blob' },
    )
    const cd = (response.headers as any)?.['content-disposition'] as string | undefined
    const m = cd?.match(/filename="?([^"]+)"?/)
    const stamp = new Date().toISOString().slice(0, 10)
    return {
      blob: response.data as Blob,
      filename: m?.[1] || `campaign-${campaignId}-reports-${stamp}.zip`,
    }
  },

  /**
   * Download a ZIP of per-subject PDF reports for a campaign —
   * SYNCHRONOUS build, kept for small campaigns / direct API use.
   * Prefer createReportJob + polling at line-manager scale.
   *
   * Same gating as the xlsx export (draft requires test_launched_at).
   */
  async exportCampaignReports(
    campaignId: string,
    opts?: { includeTest?: boolean; minN?: number },
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams()
    if (opts?.includeTest) params.append('include_test', 'true')
    if (opts?.minN !== undefined) params.append('min_n', String(opts.minN))
    const qs = params.toString() ? `?${params.toString()}` : ''
    const response = await apiClient.get(
      `${BASE}/campaigns/${campaignId}/reports.zip${qs}`,
      { responseType: 'blob' },
    )
    const cd = (response.headers as any)?.['content-disposition'] as string | undefined
    const m = cd?.match(/filename="?([^"]+)"?/)
    const stamp = new Date().toISOString().slice(0, 10)
    return {
      blob: response.data as Blob,
      filename: m?.[1] || `campaign-${campaignId}-reports-${stamp}.zip`,
    }
  },

  /**
   * Download an anonymised xlsx export of a campaign's responses.
   *
   * - On draft campaigns with `test_launched_at` set: pulls test-cohort
   *   responses only (filename suffixed `-test`).
   * - On launched/closed campaigns: real responses; pass `includeTest=true`
   *   to append the test cohort on a separate sheet.
   * - `minN` is the anonymity threshold for redacting per-response rows
   *   (default 3 on the server; bypass with `minN=1` for HR investigations).
   */
  async exportCampaign(
    campaignId: string,
    opts?: { includeTest?: boolean; minN?: number },
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams({ format: 'xlsx' })
    if (opts?.includeTest) params.append('include_test', 'true')
    if (opts?.minN !== undefined) params.append('min_n', String(opts.minN))
    const response = await apiClient.get(
      `${BASE}/campaigns/${campaignId}/export?${params.toString()}`,
      { responseType: 'blob' },
    )
    const cd = (response.headers as any)?.['content-disposition'] as string | undefined
    const m = cd?.match(/filename="?([^"]+)"?/)
    const stamp = new Date().toISOString().slice(0, 10)
    return {
      blob: response.data as Blob,
      filename: m?.[1] || `campaign-${campaignId}-${stamp}.xlsx`,
    }
  },
}

export default feedbackCampaignsService
